import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Home } from 'lucide-react';
import { useOpenCompanyOverview } from './use-open-company-overview.js';
import { useOpenHomeRecord, type HomeTarget } from './use-open-home-record.js';

// Right-click action sheet for care-home rows (Homes screener, Home record
// neighbours). Same chrome as EntityRowContextMenu.
//
//   Open home      → Home record on this home (reuses an open one)
//   Open operator  → operator Record (only when the home has an in-scope
//                    linked operator company)
//
// Open landlord (step 6, Landlords panel) and Add to Ward (Ward holding homes)
// join this menu when those exist; they are deliberately absent until then.

export function HomeRowContextMenu({ home, children }: { home: HomeTarget; children: ReactNode }) {
  const { onContextMenu, menu } = useHomeRowMenu(home);
  return (
    <>
      <div onContextMenu={onContextMenu} style={{ display: 'contents' }}>
        {children}
      </div>
      {menu}
    </>
  );
}

/** The menu as a hook, for rows that cannot take a wrapper element (a table
 *  <tr>): spread `onContextMenu` on the row and render `menu` (a portal) next
 *  to it. */
export function useHomeRowMenu(home: HomeTarget): {
  onContextMenu: (e: React.MouseEvent) => void;
  menu: ReactNode;
} {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const openHome = useOpenHomeRecord();
  const openOperator = useOpenCompanyOverview();

  useEffect(() => {
    if (!pos) return undefined;
    function onDown(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node)) return;
      setPos(null);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setPos(null);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [pos]);

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPos({ x: e.clientX, y: e.clientY });
  };

  const menu = pos
    ? createPortal(
        <div
          ref={ref}
          role="menu"
          aria-label={home.name ?? home.homeKey}
          style={{
            position: 'fixed',
            top: pos.y,
            left: pos.x,
            minWidth: '200px',
            backgroundColor: 'var(--menu-surface)',
            border: '1px solid var(--hairline)',
            borderRadius: '2px',
            boxShadow: '0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08)',
            zIndex: 9999,
            padding: 'var(--s-1)',
            fontSize: 'var(--t-body)',
          }}
        >
          <MenuItem
            icon={<Home size={12} />}
            onClick={() => {
              openHome(home);
              setPos(null);
            }}
          >
            Open home
          </MenuItem>
          {home.operatorEntityId && (
            <MenuItem
              icon={<Building2 size={12} />}
              onClick={() => {
                openOperator(home.operatorEntityId!);
                setPos(null);
              }}
            >
              Open operator
            </MenuItem>
          )}
        </div>,
        document.body,
      )
    : null;

  return { onContextMenu, menu };
}

function MenuItem({
  icon,
  onClick,
  children,
}: {
  icon: ReactNode;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        width: '100%',
        alignItems: 'center',
        gap: 'var(--s-2)',
        padding: '4px 8px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--text-primary)',
        fontSize: 'inherit',
        textAlign: 'left',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--hover-subtle)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}
